import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { PCAgentStatus, HeadsetStatus, DiagnosticsReport, LaunchArgsManifest, HmdIpdStatus } from '../types/database';
import { mockLaunchActive } from '../utils/env';

// The agent's crash_recovery block on GET /api/status (shape in
// types/database, next to the other status blocks). Re-exported here so the
// tiles can type it from the same module they talk to the agent through.
// Absent on an older agent: undefined = no crash on record.
export type { CrashRecovery } from '../types/database';

// A PICO headset agent connected to the room's RealityAgent (GET /api/hmd).
export interface HmdScreenshotResult {
  serial: string;
  saved_on_headset: boolean;
  headset_path?: string;
  width?: number;
  height?: number;
  bytes?: number;
  storage_path?: string;
  url?: string;
  image_b64?: string;
  upload_error?: string;
}

export interface HmdInfo {
  serial: string;
  mac?: string;
  ip?: string;
  pui?: string;
  agent_version?: string;
  map_md5?: string;
  battery?: number;
  charging?: boolean;
  worn?: boolean;
  /** Guardian "fence is drawn" flag (shell >= 1.0.12); absent on older shells. */
  boundary?: boolean;
  boundary_at?: string;
  connected_at: string;
  last_seen: string;
}

export interface HmdListData {
  headsets: HmdInfo[];
  /** Serial Business Streaming reports as paired to this PC; null when not streaming. */
  paired_serial: string | null;
  /** Serials with a live view open on this PC (agent with api/hmdcast.go). */
  casting?: string[] | null;
}

// One headset pose for the playspace map (GET /api/hmd/poses). Local tracking
// frame in metres (OpenXR: y up, forward -z); yaw in degrees, 0 = room north.
export interface HmdPosePoint {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface HmdPoseRow {
  serial: string;
  worn?: boolean;
  battery?: number;
  /** The headset matched its boundary map, so the pose means something in room terms. */
  relocalised: boolean;
  /** Age of the pose when the agent answered (ms). Absent with the pose. */
  age_ms?: number;
  pose?: HmdPosePoint;
  /** The headset shell refused or ignored the pose stream (old shell). */
  stream_error?: string;
}

export interface HmdPosesData {
  /** This PC's player seat (0 = unknown). */
  seat: number;
  paired_serial: string | null;
  lease_seconds: number;
  headsets: HmdPoseRow[];
}

// One seat as the room's RealityServer reports it (GET /api/mp/positions,
// relayed by any room PC's agent). Unreal world units: centimetres, degrees.
export interface MpSeatPosition {
  seat: number;
  /** A game packet within 5 s, or a served headset shadow. */
  active: boolean;
  /** The seat is currently served by its headset shadow (the game is silent). */
  shadowed: boolean;
  since_ms: number;
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  roll: number;
}

export interface MpPositionsData {
  server_ip: string;
  server_version?: string;
  /** The answering PC's own seat (0 = unknown). */
  my_seat: number;
  seats: MpSeatPosition[];
  at_ms: number;
}

export type MpPositionsResult = { ok: true; data: MpPositionsData } | { ok: false; error: string };

// One headset as the room primary sees it: live info + desired PC + file state.
export type HmdOutcome =
  | 'unassigned' | 'other_network' | 'pc_no_ip' | 'offline' | 'read_failed'
  | 'no_all_files_access' | 'in_sync' | 'drift' | 'written' | 'write_failed';

export interface HmdAssignmentRow extends HmdInfo {
  label?: string;
  assigned_pc_id?: string;
  assigned_pc_name?: string;
  assigned_ip?: string;
  bs_ip?: string;
  outcome: HmdOutcome | string;
  last_error?: string;
  checked_at?: string;
  /** All-files grant routine (agent api/hmdprovision.go), present while not idle. */
  provision?: { phase: 'reboot_pending' | 'awaiting_adb' | 'done' | 'manual' | string; since: string; attempts?: number; error?: string };
}

export interface HmdAssignmentsData {
  is_primary: boolean;
  network_id: string;
  headsets: HmdAssignmentRow[];
}

interface LaunchExperienceRequest {
  experience_id: string;
  version?: string;
  operator_id?: string;
  language?: string;
  skip_billing?: boolean;
  scene_index?: number; // Start at this scene/level (reuses the crash-resume arg); skips scene 0's touch-to-start gate for auto tests.
  args?: Record<string, string>;
  // Soak/test launches (agent ≥ 1.2.189): crashes are recorded (crash row,
  // incident, log bundle) but the agent never auto-relaunches and never rings
  // the crash alarm — the orchestrator owns the lifecycle. The run row is
  // created non-billable.
  no_auto_restart?: boolean;
}

interface LaunchExperienceResponse {
  success: boolean;
  message: string;
  error?: string;
  pid?: number;
}

interface KillExperienceRequest {
  force?: boolean;
  operator_id?: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Options for captureIncident (POST /api/incidents). Serialized body keys:
// note, severity, category, operator_id.
export interface CaptureIncidentOptions {
  note?: string;
  severity?: string;
  category?: string;
  operatorId?: string;
  /** Operator build that marked the issue; defaults to this bundle's version. */
  operatorVersion?: string;
}

interface LaunchServerRequest {
  experience_id: string;
  version?: string;
  operator_id?: string;
  session_id?: string;
  language?: string;
  args?: Record<string, string>;
}

interface LaunchServerResponse {
  success: boolean;
  server_id?: string;
  pid?: number;
  error?: string;
}

interface ServerStatus {
  id: string;
  name: string;
  version: string;
  pid: number;
  status: string;
  start_time: string;
  session_id?: string;
}

// Full timeout for normal operations (launch/kill can be slow on the agent).
const DEFAULT_TIMEOUT_MS = 15000;
// Short timeout used when probing a host we are NOT confident in (e.g. the LAN
// IP of a PC that is currently off-LAN). Lets us fail over to Tailscale quickly
// instead of blocking on a dead host for the full timeout. A live LAN answers
// /status in well under a second, so 2s is ample to detect it while keeping the
// off-LAN probe penalty (cold client + once-a-minute re-probe) small.
const PROBE_TIMEOUT_MS = 2000;
// After failing over off the primary (LAN) host, wait this long before trying
// the LAN again first — so a PC that returns to the LAN is picked back up,
// without paying a LAN timeout on every single request in between.
const LAN_REPROBE_INTERVAL_MS = 60000;

// Distinguish a request that TIMED OUT (the agent may have received and acted
// on it) from a connection-level failure (it never arrived). axios sets code
// ECONNABORTED on timeout; we also match the message defensively.
function isTimeoutError(err: any): boolean {
  return err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT' || /timeout/i.test(err?.message || '');
}

/**
 * Talks to a single PC's RealityAgent over one of several candidate hosts
 * (typically [LAN IP, Tailscale IP], in priority order).
 *
 * Behaviour:
 *  - Tries the LAN first; on a network failure (timeout/refused/unreachable —
 *    NOT an HTTP error) it falls back to Tailscale, and vice-versa.
 *  - Remembers which host last worked and tries it first next time, so once a
 *    PC is found to be off-LAN we go straight to Tailscale instead of eating a
 *    LAN timeout on every status poll and command.
 *  - Periodically (LAN_REPROBE_INTERVAL_MS) re-tries the LAN first so a PC that
 *    comes back onto the LAN is detected again. That single re-probe uses the
 *    short PROBE_TIMEOUT_MS so it's cheap when the LAN is still unreachable.
 *  - When given a cacheKey, persists the last-good host in localStorage and
 *    restores it on construction, so a fresh client (e.g. after a page refresh
 *    by a remote operator) starts straight on Tailscale instead of paying the
 *    LAN probe every time.
 */
export class PCAgentClient {
  private hosts: string[];
  private port: number;
  private timeout: number;
  // Index into `hosts` we try first. 0 == primary (LAN).
  private preferredIndex = 0;
  // Hosts proven reachable at least once recently — they get the full timeout;
  // unproven hosts only get the short probe timeout (unless they're the last
  // candidate, which always gets the full timeout as a final attempt).
  private confirmed = new Set<number>();
  // Earliest time (ms epoch) we'll re-probe the LAN first after a failover.
  private lanReprobeAt = 0;
  // Optional key for persisting the last-good host across client instances.
  private cacheKey?: string;
  // Host string we last wrote to storage, to avoid redundant writes.
  private persistedHost?: string;

  constructor(hosts: string | Array<string | undefined | null>, port: number = 9090, timeout: number = DEFAULT_TIMEOUT_MS, cacheKey?: string) {
    const list = (Array.isArray(hosts) ? hosts : [hosts])
      .map((h) => (h || '').trim())
      .filter((h) => h.length > 0);
    // De-dupe while preserving order (LAN first, Tailscale second).
    this.hosts = Array.from(new Set(list));
    this.port = port;
    this.timeout = timeout;
    this.cacheKey = cacheKey;
    this.restorePreference();
  }

  private storageKey(): string {
    return `ra:pcpref:${this.cacheKey}`;
  }

  // Restore the last-good host (if any) so a cold client skips the LAN probe.
  private restorePreference() {
    if (!this.cacheKey || this.hosts.length <= 1) return;
    try {
      const saved = globalThis.localStorage?.getItem(this.storageKey());
      if (!saved) return;
      const idx = this.hosts.indexOf(saved);
      // idx <= 0 means: not found (IP changed) OR it's the LAN default. We only
      // restore a NON-LAN (fallback) preference — the LAN is already tried first
      // by default and should stay fast-probed, not trusted outright (otherwise
      // an on-LAN -> remote move would eat a full LAN timeout on first load).
      if (idx <= 0) return;
      this.preferredIndex = idx;
      this.confirmed.add(idx);
      this.persistedHost = saved;
      // Known-good fallback host: don't re-probe the LAN on the very first
      // request — go straight to it. LAN is still re-probed after the cooldown.
      this.lanReprobeAt = Date.now() + LAN_REPROBE_INTERVAL_MS;
    } catch {
      // localStorage unavailable — ignore, just lose the optimization.
    }
  }

  private persistPreference(host: string) {
    if (!this.cacheKey || !host || host === this.persistedHost) return;
    this.persistedHost = host;
    try {
      globalThis.localStorage?.setItem(this.storageKey(), host);
    } catch {
      // localStorage unavailable — ignore.
    }
  }

  /** Host currently tried first — for WebSocket / direct-fetch consumers. */
  getActiveHost(): string {
    return this.hosts[this.preferredIndex] || this.hosts[0] || '';
  }

  /** All configured hosts, for logging/diagnostics. */
  private hostLabel(): string {
    return this.hosts.join('/') || '(no host)';
  }

  /**
   * Candidate host indices for this attempt, in the order to try them.
   * Reads (idempotent) periodically re-probe the LAN first; writes always go
   * straight to the preferred host (they must never be sent to a presumed-dead
   * LAN first, where a slow no-response could trigger a duplicate launch).
   */
  private candidateOrder(idempotent: boolean): number[] {
    const all = this.hosts.map((_, i) => i);
    if (all.length <= 1) return all;
    // On the LAN (or never failed over): primary first.
    if (this.preferredIndex === 0) return all;
    // Failed over to a non-primary host. For reads, re-probe the primary first
    // once the cooldown has elapsed — and immediately "claim" that re-probe by
    // advancing lanReprobeAt, so concurrent reads in the same tick don't each
    // independently probe the dead LAN.
    if (idempotent && Date.now() >= this.lanReprobeAt) {
      this.lanReprobeAt = Date.now() + LAN_REPROBE_INTERVAL_MS;
      return all;
    }
    return [this.preferredIndex, ...all.filter((i) => i !== this.preferredIndex)];
  }

  private markReachable(hostIndex: number) {
    this.confirmed.add(hostIndex);
    this.preferredIndex = hostIndex;
    if (hostIndex === 0) this.lanReprobeAt = 0;
    this.persistPreference(this.hosts[hostIndex]);
  }

  private markUnreachable(hostIndex: number) {
    this.confirmed.delete(hostIndex);
    // If the primary just failed, hold off re-probing it for a while.
    if (hostIndex === 0) this.lanReprobeAt = Date.now() + LAN_REPROBE_INTERVAL_MS;
  }

  /**
   * Issue a request, trying each candidate host in order until one answers.
   * A host that returns an HTTP response (even 4xx/5xx) counts as reachable —
   * we surface that error rather than failing over, because the agent answered.
   * Only transport failures (no response) trigger a fallback to the next host.
   */
  private async send<T = any>(config: AxiosRequestConfig, opts: { idempotent?: boolean; timeoutMs?: number } = {}): Promise<AxiosResponse<T>> {
    const idempotent = opts.idempotent === true;
    const order = this.candidateOrder(idempotent);
    if (order.length === 0) {
      throw new Error('No host configured for PC agent');
    }
    let lastError: any;
    for (let pos = 0; pos < order.length; pos++) {
      const hostIndex = order[pos];
      const host = this.hosts[hostIndex];
      // Timeout policy: ONLY an idempotent read against the LAN primary (index 0)
      // that is NOT the trusted/preferred host gets the short probe timeout, so
      // reachability is detected fast. Everything else gets the full timeout:
      //  - a write can be slow (launch spawns Unreal) and must not be aborted
      //    early — an early abort followed by a failover would re-send the
      //    launch and the agent would report "already running" (a false FAIL);
      //  - the Tailscale fallback must never be clipped (a remote operator's
      //    Tailscale RTT can exceed the probe window; clipping it made the whole
      //    room flap between "all off" and "all on").
      const lanIsTrusted = this.preferredIndex === 0 && this.confirmed.has(0);
      const useProbeTimeout = idempotent && hostIndex === 0 && !lanIsTrusted;
      // opts.timeoutMs overrides for the few calls the agent answers slowly by
      // design (bs-restart waits up to 60 s for the BS process and 30 s for the
      // headset to re-attach). Without it the 15 s default aborts a call that is
      // succeeding, and the operator is told it failed.
      const attemptTimeout = opts.timeoutMs ?? (useProbeTimeout ? PROBE_TIMEOUT_MS : this.timeout);
      try {
        const res = await axios.request<T>({
          ...config,
          baseURL: `http://${host}:${this.port}`,
          timeout: attemptTimeout,
          headers: { 'Content-Type': 'application/json', ...(config.headers as Record<string, string> | undefined) },
        });
        this.markReachable(hostIndex);
        return res;
      } catch (error: any) {
        if (error?.response) {
          // Agent responded with an error status — it IS reachable.
          this.markReachable(hostIndex);
          throw error;
        }
        // A write that TIMED OUT on a host we've CONFIRMED is live may have
        // reached the agent and already acted on it (e.g. the launch is now
        // running). Retrying on another host would duplicate the side effect, so
        // do NOT fail over — and do NOT demote the host (it may just be slow).
        // A timeout on an UNconfirmed host (we've never reached it) most likely
        // means it's unreachable, so failing over is safe — this is what lets a
        // fresh off-LAN client (multiplayer/shutdown flows) reach Tailscale.
        // Any connection-level failure also fails over: the request never took
        // effect.
        if (!idempotent && isTimeoutError(error) && this.confirmed.has(hostIndex)) {
          throw error;
        }
        this.markUnreachable(hostIndex);
        lastError = error;
      }
    }
    throw lastError;
  }

  // Get PC status
  async getStatus(): Promise<PCAgentStatus | null> {
    try {
      const { data } = await this.send<ApiResponse<PCAgentStatus>>({ method: 'GET', url: '/api/status' }, { idempotent: true });
      return data.data || null;
    } catch (error) {
      console.error(`Error getting status from ${this.hostLabel()}:`, error);
      return null;
    }
  }

  // Get available experiences on this PC
  async getExperiences(): Promise<any[]> {
    try {
      const { data } = await this.send<ApiResponse<any[]>>({ method: 'GET', url: '/api/experiences' }, { idempotent: true });
      return data.data || [];
    } catch (error) {
      console.error(`Error getting experiences from ${this.hostLabel()}:`, error);
      return [];
    }
  }

  // Fetch the LaunchArgs.json manifest shipped inside the installed build of
  // an experience (typed schema the launch-args form renders from). Returns
  // null when the experience isn't installed on this PC or the build ships no
  // manifest — the caller then tries another PC or falls back to raw JSON.
  async getLaunchArgsSchema(experienceId: string, version?: string): Promise<LaunchArgsManifest | null> {
    try {
      const { data } = await this.send<ApiResponse<LaunchArgsManifest>>(
        {
          method: 'GET',
          url: '/api/experiences/launch-args-schema',
          params: { experience_id: experienceId, ...(version ? { version } : {}) },
        },
        { idempotent: true }
      );
      return data.data || null;
    } catch (error) {
      console.warn(`No launch-args schema from ${this.hostLabel()}:`, error);
      return null;
    }
  }

  // Launch an experience
  async launchExperience(request: LaunchExperienceRequest): Promise<LaunchExperienceResponse> {
    // Dev/staging shortcut for testing flows that don't require an actual PC
    // agent (queue pop, audit attribution, lifecycle UI). Enable in DevTools
    // (localStorage.setItem('MOCK_LAUNCH', '1')) or Settings -> Staging tools.
    // Gated on DEV_TOOLS_ENABLED (dev server or staging bundle), so a prod
    // build can never accidentally skip the real launch. killExperience shares
    // the guard so the queue recovery actions (restart / move / requeue) can be
    // drilled end-to-end without a PC.
    if (mockLaunchActive()) {
      console.warn(`[MOCK_LAUNCH] short-circuiting launchExperience for ${this.hostLabel()}`, request);
      return {
        success: true,
        message: 'Mock launch - no PC agent contacted',
        pid: 99999,
      } as LaunchExperienceResponse;
    }
    try {
      const { data } = await this.send<LaunchExperienceResponse>({
        method: 'POST',
        url: '/api/experiences/launch',
        data: request,
      });
      return data;
    } catch (error: any) {
      console.error(`Error launching experience on ${this.hostLabel()}:`, error);
      return {
        success: false,
        message: 'Failed to launch experience',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Kill running experience
  async killExperience(force: boolean = false, operatorId?: string): Promise<ApiResponse> {
    if (mockLaunchActive()) {
      console.warn(`[MOCK_LAUNCH] short-circuiting killExperience for ${this.hostLabel()}`, { force, operatorId });
      return { success: true, message: 'Mock kill - no PC agent contacted' } as ApiResponse;
    }
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: '/api/experiences/kill',
        data: { force, operator_id: operatorId },
      });
      return data;
    } catch (error: any) {
      console.error(`Error killing experience on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Mark the running experience's run as completed (normally the game calls
  // this itself at the end; the Test Lab soak calls it after the final scene's
  // soak window passes, BEFORE killing, so the run row gets completed_at and
  // the kill's abort path no-ops).
  async completeExperience(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/experiences/complete' });
      return data;
    } catch (error: any) {
      console.error(`Error completing experience on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Invalidate running experience run (admin only - deletes run from database)
  async invalidateExperienceRun(operatorId?: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: '/api/experiences/invalidate-run',
        data: { operator_id: operatorId },
      });
      return data;
    } catch (error: any) {
      console.error(`Error invalidating experience run on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Get volume
  async getVolume(): Promise<number | null> {
    try {
      const { data } = await this.send<ApiResponse<{ level: number }>>({ method: 'GET', url: '/api/volume' }, { idempotent: true });
      return data.data?.level ?? null;
    } catch (error) {
      console.error(`Error getting volume from ${this.hostLabel()}:`, error);
      return null;
    }
  }

  // Set volume
  async setVolume(level: number): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/volume', data: { level } });
      return data;
    } catch (error: any) {
      console.error(`Error setting volume on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Play a preset voice warning to the visitor in the headset. The agent
  // resolves the language and clip; every language travels with the request so
  // the agent never needs the operator_messages table, and the per-language
  // clip_version is what keeps its clip cache honest. text_nl/text_en/
  // clip_version are the legacy mirror for agents older than the multilingual
  // release. Responds with status:"playing" immediately; completion arrives as
  // a `visitor_message` WebSocket event. 409 = a message is already playing.
  async sendVisitorMessage(msg: {
    key: string;
    texts: Record<string, { text: string; clip_version: number }>;
    titles: Record<string, string>;
    text_nl: string;
    text_en: string;
    clip_version: number;
    operator: string;
  }): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/message/send', data: msg });
      return data;
    } catch (error: any) {
      console.error(`Error sending visitor message on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Call a function of the headset shell's IToBServiceProxy bench command
  // (shell 1.0.7+): setipd, resettracking, message, ... The agent allow-lists
  // "proxy"; the shell rejects unknown fn values.
  async hmdProxy(serial: string, fn: string, args: Record<string, string | number> = {}): Promise<ApiResponse> {
    try {
      const a: Record<string, string> = { fn };
      for (const [k, v] of Object.entries(args)) a[k] = String(v);
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: `/api/hmd/${encodeURIComponent(serial)}/cmd`,
        data: { name: 'proxy', args: a },
      });
      return data;
    } catch (error: any) {
      console.error(`Error calling headset proxy ${fn} on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Read or set one of the headset's system function switches (ToBService
  // PBS_SystemFunctionSwitchEnum name, e.g. SFS_GLOBAL_VST). Without "on"
  // the shell only reads; the answer carries {fn, set, status: 0|1}.
  async hmdSwitch(serial: string, fn: string, on?: boolean): Promise<ApiResponse<{ fn: string; set?: string; status?: number }>> {
    try {
      const args: Record<string, string> = { fn };
      if (on !== undefined) args.on = on ? '1' : '0';
      const { data } = await this.send<ApiResponse<{ fn: string; set?: string; status?: number }>>({
        method: 'POST',
        url: `/api/hmd/${encodeURIComponent(serial)}/cmd`,
        data: { name: 'switch', args },
      });
      return data;
    } catch (error: any) {
      console.error(`Error switching ${fn} on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Headset IPD lock (agent >= 1.2.207, api/hmdipd.go). `manual: true` is kid
  // mode: the agent stops re-sending its 62 mm for the current run so the
  // buttons on the headset can reach 58. `mm` (62..72) is sent at once and
  // held from then on. Either way the reply carries the resulting state; a
  // failed immediate send (headset offline) still records the flag and comes
  // back as success:false WITH data. 404 = agent too old.
  async setHmdIpd(body: { manual?: boolean; mm?: number; operator?: string }): Promise<ApiResponse<HmdIpdStatus>> {
    try {
      const { data } = await this.send<ApiResponse<HmdIpdStatus>>({ method: 'POST', url: '/api/hmd/ipd', data: body });
      return data;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { success: false, error: 'agent_too_old' };
      }
      console.error(`Error setting headset IPD mode on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Get headset status
  async getHeadsetStatus(): Promise<HeadsetStatus | null> {
    try {
      const { data } = await this.send<HeadsetStatus>({ method: 'GET', url: '/api/headset/status' }, { idempotent: true });
      return data;
    } catch (error) {
      console.error(`Error getting headset status from ${this.hostLabel()}:`, error);
      return null;
    }
  }

  // ---- PICO headset agents (RealityAgent /api/hmd) ------------------------------
  // Headsets dial the room agent over a socket and are addressed by serial; the
  // operator never sees a headset IP. `paired_serial` is the headset Business
  // Streaming reports as connected to THIS PC (live signal, null when not streaming).

  async getHmdList(): Promise<HmdListData | null> {
    try {
      const { data } = await this.send<ApiResponse<HmdListData>>({ method: 'GET', url: '/api/hmd' }, { idempotent: true });
      return data.success && data.data ? data.data : null;
    } catch (error) {
      // Older agents have no /api/hmd — treat as "no headset agents", not an error.
      return null;
    }
  }

  // Playspace map: last streamed pose of every headset whose socket this PC
  // holds. Polling this IS the lease that keeps the headsets streaming (agent
  // api/hmdmap.go); stop polling and the streams stop by themselves.
  // Returns null on an agent without the endpoint (too old for the map) and
  // throws when the PC cannot be reached at all, so the map can tell the two apart.
  // Where every seat in the session is, straight from the room's RealityServer
  // (the game itself reports its pose there; no headset shell needed). Any room
  // PC can answer since the agent is only the UDP bridge to the NUC. Resolves
  // with ok=false + a code when the PC answered but the server did not
  // (no_server_ip, server_unreachable, server_too_old, agent_too_old); throws
  // only when the PC itself cannot be reached, so the map can try the next PC.
  async getMpPositions(): Promise<MpPositionsResult> {
    try {
      const { data } = await this.send<ApiResponse<MpPositionsData>>({ method: 'GET', url: '/api/mp/positions' }, { idempotent: true });
      if (data.success && data.data) return { ok: true, data: data.data };
      return { ok: false, error: data.error || 'unknown' };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return { ok: false, error: 'agent_too_old' };
      throw error;
    }
  }

  async getHmdPoses(): Promise<HmdPosesData | null> {
    try {
      const { data } = await this.send<ApiResponse<HmdPosesData>>({ method: 'GET', url: '/api/hmd/poses' }, { idempotent: true });
      return data.success && data.data ? data.data : null;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    }
  }

  // Kill + relaunch the Business Streaming client on the headset (the remote cure
  // for a headset that will not reconnect to the PC).
  async hmdRestartStreaming(serial: string): Promise<ApiResponse> {
    return this.hmdPost(serial, 'restart-streaming');
  }

  // Restart Business Streaming on BOTH sides: the agent restarts BS on the PC,
  // waits for the process, then kicks the paired headset and waits for it to
  // re-attach. Unlike hmdRestartStreaming this takes no serial — which is the
  // point. The headset-only call reads its serial FROM Business Streaming, so
  // when the PC side is what died there is no serial and the cheap lever cannot
  // fire at all (Heuvel1 2026-09-15). The agent falls back to the headset
  // registry for the kick target.
  //
  // Replies with the agent's raw shape ({ok, error, hmd_kicked, hmd_attached}),
  // not the usual {success, data} envelope, so it is mapped here.
  async repairBSRestart(): Promise<ApiResponse<{ hmd_kicked?: string; hmd_attached?: boolean }>> {
    try {
      const { data } = await this.send<{ ok?: boolean; error?: string; hmd_kicked?: string; hmd_attached?: boolean }>({
        method: 'POST', url: '/api/repair/bs-restart', data: {},
      }, { timeoutMs: 95000 });
      return {
        success: !!data?.ok,
        error: data?.ok ? undefined : (data?.error || 'bs-restart mislukt'),
        data: { hmd_kicked: data?.hmd_kicked, hmd_attached: data?.hmd_attached },
      };
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { success: false, error: 'agent_too_old' };
      }
      console.error(`Error restarting Business Streaming on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async hmdReboot(serial: string): Promise<ApiResponse> {
    return this.hmdPost(serial, 'reboot');
  }

  // Screenshot: shell >= 0.19.0 returns the capture inline; the agent stores it
  // in the incident bucket and returns a signed URL (or the image itself when
  // it has no uploader). Older shells only save to the headset gallery.
  async hmdScreenshot(serial: string): Promise<ApiResponse<HmdScreenshotResult>> {
    return this.hmdPost(serial, 'screenshot') as Promise<ApiResponse<HmdScreenshotResult>>;
  }

  // Live view (HmdCastView): the viewer socket itself starts and ends the cast;
  // this ends it explicitly (used when a viewer switches quality).
  async hmdCastStop(serial: string): Promise<ApiResponse> {
    return this.hmdPost(serial, 'cast/stop');
  }

  // Assignment view served by the room PRIMARY: each connected headset with its
  // desired PC (from infra.hmd_devices) and what its BStreaming.json says.
  async getHmdAssignments(): Promise<HmdAssignmentsData | null> {
    try {
      const { data } = await this.send<ApiResponse<HmdAssignmentsData>>({ method: 'GET', url: '/api/hmd/assignments' }, { idempotent: true });
      return data.success && data.data ? data.data : null;
    } catch (error) {
      return null; // older agent, or not reachable
    }
  }

  // Rewrite the headset's BStreaming.json to its desired PC and restart its BS
  // client. Manual by design: it restarts that guest's streaming.
  async hmdPush(serial: string): Promise<ApiResponse<HmdAssignmentRow>> {
    return this.hmdPost(serial, 'push');
  }

  // Start the all-files grant routine on a held headset (wireless adb on,
  // reboot, appops over adb, off). 409 with the reason when the safety gate
  // refuses (worn, experience running, already provisioning).
  async hmdProvision(serial: string): Promise<ApiResponse> {
    return this.hmdPost(serial, 'provision');
  }

  async hmdPushAll(): Promise<ApiResponse<Record<string, HmdAssignmentRow>>> {
    try {
      const { data } = await this.send<ApiResponse<Record<string, HmdAssignmentRow>>>({ method: 'POST', url: '/api/hmd/push' });
      return data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  private async hmdPost(serial: string, action: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: `/api/hmd/${encodeURIComponent(serial)}/${action}`,
      });
      return data;
    } catch (error: any) {
      console.error(`Error sending ${action} to headset ${serial} via ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Poll until the dedicated server reports "server_ready" status
  async waitForServerReady(timeoutMs: number = 60000): Promise<boolean> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const status = await this.getStatus();
      if (status?.current_experience?.status === 'server_ready') {
        return true;
      }
      // If experience stopped running (crash/failure), bail early
      if (status && !status.current_experience) {
        return false;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    return false; // Timeout
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const { data } = await this.send<{ status?: string }>({ method: 'GET', url: '/health' }, { idempotent: true });
      return data.status === 'ok';
    } catch (error) {
      return false;
    }
  }

  // Trigger experience download/sync check (monitor)
  async triggerExperienceUpdate(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/experiences/trigger-update' });
      return data;
    } catch (error: any) {
      console.error(`Error triggering experience update on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Trigger agent self-update
  async triggerUpdate(force: boolean = false): Promise<ApiResponse> {
    try {
      const url = force ? '/api/update/trigger?force=true' : '/api/update/trigger';
      const { data } = await this.send<ApiResponse>({ method: 'POST', url });
      return data;
    } catch (error: any) {
      console.error(`Error triggering update on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Check for updates
  async checkUpdate(): Promise<ApiResponse<{ update_available: boolean; current_version: string; latest_version?: string }>> {
    try {
      const { data } = await this.send<ApiResponse<{ update_available: boolean; current_version: string; latest_version?: string }>>({
        method: 'GET',
        url: '/api/update/check',
      }, { idempotent: true });
      return data;
    } catch (error: any) {
      console.error(`Error checking update on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Shutdown PC
  async shutdown(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/shutdown' });
      return data;
    } catch (error: any) {
      console.error(`Error shutting down ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Restart RealityAgent service
  async restartAgent(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/restart' });
      return data;
    } catch (error: any) {
      console.error(`Error restarting agent on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Trigger agent self-update to a specific version (used by the Software page
  // to push a room's desired agent version to a PC).
  async updateAgentVersion(version: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: '/api/update/trigger-version',
        data: { version },
      });
      return data;
    } catch (error: any) {
      console.error(`Error updating agent to ${version} on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // --- PICO Business Streaming settings (agent /api/streaming/* routes) ---

  // Current local Business Streaming settings per user profile + running state.
  async getStreamingSettings(): Promise<ApiResponse> {
    try {
      const { data } = await this.send({ method: 'GET', url: '/api/streaming/settings' }, { idempotent: true });
      return { success: true, data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Re-apply the desired room/PC Business Streaming settings from the DB now.
  // The agent restarts Business Streaming when values changed — this interrupts
  // an active headset stream.
  async applyStreamingSettings(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<any>({ method: 'POST', url: '/api/streaming/settings/apply' });
      if (data?.success === false) {
        return { success: false, error: data.error || 'apply failed', data };
      }
      return { success: true, data };
    } catch (error: any) {
      console.error(`Error applying streaming settings on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // --- NGRConvai control (proxied through the agent's /api/convai/* routes) ---

  // Normalize a convai response: the agent's lifecycle handlers return
  // {success,message}, while update/cache proxy NGRConvai's {status,...} body
  // (which can be {status:"error"} even on HTTP 200).
  private normalizeConvai(data: any): ApiResponse {
    if (data && data.status === 'error') {
      return { success: false, error: data.error || data.message || 'NGRConvai error', data };
    }
    if (data && data.success === false) {
      return { success: false, error: data.error || data.message, data };
    }
    return { success: true, data };
  }

  async getConvaiStatus(): Promise<ApiResponse> {
    try {
      const { data } = await this.send({ method: 'GET', url: '/api/convai/status' }, { idempotent: true });
      return { success: true, data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async updateConvai(version?: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send({ method: 'POST', url: '/api/convai/update', data: version ? { version } : {} });
      return this.normalizeConvai(data);
    } catch (error: any) {
      console.error(`Error updating NGRConvai on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async restartConvai(): Promise<ApiResponse> {
    try {
      const { data } = await this.send({ method: 'POST', url: '/api/convai/restart' });
      return this.normalizeConvai(data);
    } catch (error: any) {
      console.error(`Error restarting NGRConvai on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async startConvai(): Promise<ApiResponse> {
    try {
      const { data } = await this.send({ method: 'POST', url: '/api/convai/start' });
      return this.normalizeConvai(data);
    } catch (error: any) {
      console.error(`Error starting NGRConvai on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async stopConvai(): Promise<ApiResponse> {
    try {
      const { data } = await this.send({ method: 'POST', url: '/api/convai/stop' });
      return this.normalizeConvai(data);
    } catch (error: any) {
      console.error(`Error stopping NGRConvai on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Clear the NGRConvai audio cache. experience "" clears the whole cache.
  async clearConvaiCache(experience?: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send({ method: 'POST', url: '/api/convai/cache/clear', data: experience ? { experience } : {} });
      return this.normalizeConvai(data);
    } catch (error: any) {
      console.error(`Error clearing NGRConvai cache on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Restart the PC itself
  async restartPC(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/pc/restart' });
      return data;
    } catch (error: any) {
      console.error(`Error restarting PC on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Start Parsec (remote-desktop host) if it isn't already running.
  async bootParsec(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/parsec/boot' });
      return data;
    } catch (error: any) {
      console.error(`Error booting Parsec on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Restart Parsec (stop/kill then start again).
  async rebootParsec(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/parsec/reboot' });
      return data;
    } catch (error: any) {
      console.error(`Error rebooting Parsec on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Attempt auto-repair of the "experience running but no VR stream" state. The
  // agent classifies the root-cause variant (audio-sink-stale vs display-strand),
  // applies the matching fix (restart Business Streaming and/or reset the display
  // mode), then verifies by launching the boot-test experience and confirming the
  // encoder heartbeat resumed. It returns 202 immediately; progress + the final
  // outcome (recovered / needs_reboot) arrive over the websocket as
  // `repair_progress` messages. On needs_reboot the caller offers restartPC().
  // stopExperience: explicit consent to end the running session first. A running
  // experience blocks the repair agent-side, and every no-VR alert has one, so
  // without this the repair is a no-op. The caller confirms with the operator.
  async repairRecover(category?: string, stopExperience = false): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST', url: '/api/repair/recover',
        data: { ...(category ? { category } : {}), stop_experience: stopExperience },
      });
      return data;
    } catch (error: any) {
      console.error(`Error starting repair on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Dismiss a sticky agent notification by id (e.g. the no-VR 'vr_no_stream'
  // banner), clearing it agent-side so the status poll doesn't bring it back.
  async stopNotification(id: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST', url: '/api/notification/stop', data: { id },
      });
      return data;
    } catch (error: any) {
      console.error(`Error dismissing notification on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Capture an incident on this PC: the agent bundles the local logs (PICO
  // Business Streaming, SteamVR, agent) to Supabase Storage and records a row in
  // operations.incidents. Returns immediately with the incident id; the log
  // bundle uploads asynchronously and the row's link fills in shortly after.
  //
  // Accepts either the legacy positional form (note, severity) or an options
  // object with category/operatorId. Body keys sent to the agent: note,
  // severity, category, operator_id. Old agent versions silently drop unknown
  // JSON fields, so sending category/operator_id to them is safe.
  async captureIncident(
    noteOrOptions?: string | CaptureIncidentOptions,
    severity?: string
  ): Promise<ApiResponse<{ id: string; status: string; message: string }>> {
    const opts: CaptureIncidentOptions =
      typeof noteOrOptions === 'object' && noteOrOptions !== null
        ? noteOrOptions
        : { note: noteOrOptions, severity };
    try {
      const body: Record<string, string> = {};
      if (opts.note) body.note = opts.note;
      if (opts.severity) body.severity = opts.severity;
      if (opts.category) body.category = opts.category;
      if (opts.operatorId) body.operator_id = opts.operatorId;
      // The agent stamps every incident with the component versions of the
      // moment (metadata.versions); the operator version is the one it cannot know.
      const operatorVersion =
        opts.operatorVersion ?? (typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '');
      if (operatorVersion) body.operator_version = operatorVersion;
      const { data } = await this.send<{ id: string; status: string; message: string }>({
        method: 'POST',
        url: '/api/incidents',
        data: body,
      });
      return { success: true, data, message: data?.message };
    } catch (error: any) {
      console.error(`Error capturing incident on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // --- File drop (agent /api/filedrop/* routes) ---

  // Grab files FROM this PC into the file-drop bucket. paths accepts files,
  // directories, or globs on the PC. The agent answers 202 with the new drop
  // id; the operations.file_drops row flips uploading -> ready when done.
  async fileDropUpload(
    paths: string[],
    note?: string,
    name?: string
  ): Promise<ApiResponse<{ id: string; file_name: string; files: number; status: string }>> {
    try {
      const body: Record<string, unknown> = { paths };
      if (note) body.note = note;
      if (name) body.name = name;
      const { data } = await this.send<{ id: string; file_name: string; files: number; status: string }>({
        method: 'POST',
        url: '/api/filedrop/upload',
        data: body,
      });
      return { success: true, data };
    } catch (error: any) {
      console.error(`Error uploading file drop on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.response?.data || error.message };
    }
  }

  // Send a ready drop TO this PC. Lands in the agent's inbox dir
  // (C:\Reality\Inbox) unless destPath overrides it. 202 with the final path.
  async fileDropFetch(
    dropId: string,
    destPath?: string
  ): Promise<ApiResponse<{ id: string; file_name: string; dest: string; status: string }>> {
    try {
      const body: Record<string, string> = { drop_id: dropId };
      if (destPath) body.dest_path = destPath;
      const { data } = await this.send<{ id: string; file_name: string; dest: string; status: string }>({
        method: 'POST',
        url: '/api/filedrop/fetch',
        data: body,
      });
      return { success: true, data };
    } catch (error: any) {
      console.error(`Error fetching file drop on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.response?.data || error.message };
    }
  }

  // --- Autodiagnostics (agent /api/diagnostics/* routes) ---

  // Start a diagnostics run (all tests, or the given subset). The agent
  // answers 202 {success:true}, or 409 {success:false, error:"run_active"|
  // "experience_running"} which surfaces here as {success:false, error}.
  async runDiagnostics(tests?: string[]): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: '/api/diagnostics/run',
        data: tests && tests.length > 0 ? { tests } : {},
      });
      return data;
    } catch (error: any) {
      console.error(`Error starting diagnostics on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Abort the active diagnostics run ("Skip checks").
  async abortDiagnostics(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/diagnostics/abort' });
      return data;
    } catch (error: any) {
      console.error(`Error aborting diagnostics on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Override a malfunctioning verdict and unlock launches. Both fields are
  // required by the agent (400 otherwise).
  async overrideDiagnostics(operator: string, reason: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: '/api/diagnostics/override',
        data: { operator, reason },
      });
      return data;
    } catch (error: any) {
      console.error(`Error overriding diagnostics on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Pause build sync on this PC immediately (leech cancelled with progress
  // kept, seeding refused). In-memory only — write the durable
  // pc_instances.sync_paused flag via databaseService.setSyncPaused FIRST.
  // A 404 means an old agent without sync control (nothing to pause).
  async pauseSync(operator?: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: '/api/sync/pause',
        data: { operator },
      });
      return data;
    } catch (error: any) {
      console.error(`Error pausing sync on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Resume build sync (the persisted piece bitmap resumes exactly). Clear the
  // DB flag first, then poke this for immediate effect.
  async resumeSync(operator?: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: '/api/sync/resume',
        data: { operator },
      });
      return data;
    } catch (error: any) {
      console.error(`Error resuming sync on ${this.hostLabel()}:`, error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Full diagnostics run report (per-test rows) for the report modal.
  async getDiagnosticsReport(): Promise<DiagnosticsReport | null> {
    try {
      const { data } = await this.send<ApiResponse<DiagnosticsReport>>({ method: 'GET', url: '/api/diagnostics/run' }, { idempotent: true });
      return data.data || null;
    } catch (error) {
      console.error(`Error getting diagnostics report from ${this.hostLabel()}:`, error);
      return null;
    }
  }

  // Send next level command to Unreal
  async nextLevel(): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/unreal/level/next' });
      return data;
    } catch (error: any) {
      console.error(`Error sending next level command on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Send go to level command to Unreal
  async goToLevel(level: number): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: '/api/unreal/level/start', data: { level } });
      return data;
    } catch (error: any) {
      console.error(`Error sending go to level command on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // --- Dedicated Server Management (multiplayer) ---

  // Launch a dedicated server instance
  async launchServer(request: LaunchServerRequest): Promise<LaunchServerResponse> {
    try {
      const { data } = await this.send<LaunchServerResponse>({ method: 'POST', url: '/api/servers/launch', data: request });
      return data;
    } catch (error: any) {
      console.error(`Error launching server on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Get status of a specific dedicated server
  async getServerStatus(serverId: string): Promise<ServerStatus | null> {
    try {
      const { data } = await this.send<ServerStatus>({ method: 'GET', url: `/api/servers/${serverId}/status` }, { idempotent: true });
      return data;
    } catch (error) {
      console.error(`Error getting server status from ${this.hostLabel()}:`, error);
      return null;
    }
  }

  // Kill a specific dedicated server
  async killServer(serverId: string): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({ method: 'POST', url: `/api/servers/${serverId}/kill` });
      return data;
    } catch (error: any) {
      console.error(`Error killing server on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Poll until a dedicated server reports "ready" status
  async waitForDedicatedServerReady(serverId: string, timeoutMs: number = 60000): Promise<boolean> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const status = await this.getServerStatus(serverId);
      if (status?.status === 'ready') {
        return true;
      }
      // If server disappeared (crashed/killed), bail early
      if (!status) {
        return false;
      }
      if (status.status === 'killed') {
        return false;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    return false; // Timeout
  }

  // --- Discord Voice Chat ---

  // Set VC session info so RealityAgent cleans up when experience ends
  async setVcSession(guildId: string, tempChannelId: string, lobbyChannelId: string, discordUserId: string, allUserIds: string[] = []): Promise<ApiResponse> {
    try {
      const { data } = await this.send<ApiResponse>({
        method: 'POST',
        url: '/api/discord/vc-session',
        data: {
          guild_id: guildId,
          temp_channel_id: tempChannelId,
          lobby_channel_id: lobbyChannelId,
          discord_user_id: discordUserId,
          all_user_ids: allUserIds,
        },
      });
      return data;
    } catch (error: any) {
      console.error(`Error setting VC session on ${this.hostLabel()}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }
}

// Factory function to create PC agent clients. Accepts either a single host or
// an ordered list of candidate hosts (e.g. [lanIP, tailscaleIP]); the client
// tries them in order with automatic failover. Empty/undefined entries are
// ignored.
export function createPCAgentClient(hosts: string | Array<string | undefined | null>, port: number = 9090, cacheKey?: string): PCAgentClient {
  return new PCAgentClient(hosts, port, DEFAULT_TIMEOUT_MS, cacheKey);
}
